/*
 * Copyright (c) 2021. World Wide Generation Ltd
 */

import React, { useEffect, useMemo, useState } from 'react';
import Dashboard, { DashboardSection } from '../dashboard';
import { Button } from 'reactstrap';
import { generateUrl } from '../../routes/util';
import { ROUTES } from '../../constants/routes';
import { AssurancePortfolioStatus, AssuranceStatus, OrganizationAssurancePortfolio } from '../../types/assurance';
import { useHistory, useLocation } from 'react-router-dom';
import { standards } from '@g17eco/core';
import { NotApplicableBadge } from '../question/NotApplicableBadge';
import { getNaType } from '../../utils/universalTrackerValue';
import { nl2br } from '../../utils';
import { QuestionInput } from '../question/QuestionInput';
import { getQuestionId } from '../../selectors/survey';
import { NavigationButtons } from '../question/NavigationButtons';
import { SupportingEvidenceTooltip } from '../utr-confirmation-modal/SupportingEvidenceTooltip';
import { loadUtrvHistoryAssurer } from '../../actions/universalTracker';
import { UtrvHistory, ValueHistory } from '../../types/universalTrackerValue';
import { getExistingFiles } from '../survey/question/QuestionReducer';
import { ExistingEvidenceFile } from '../survey/question/questionInterfaces';
import { EvidenceContainer } from '../files/EvidenceContainer';
import { loadUniversalTrackerModal } from '../../actions/universalTrackerModal';
import CompositeValueHistory from '../value-history/CompositeValueHistory';
import { ScopeQuestionGroup } from '../../types/survey';
import { useAppDispatch } from '../../reducers';
import UtrvComments from '../utrv-comments';
import G17Client from '../../services/G17Client';
import { UtrvStatus } from '../../constants/status';
import { downloadQuestionBundle } from '../../actions/assurance';
import { getAnalytics } from '../../services/analytics/AnalyticsService';
import { AnalyticsEvents } from '../../services/analytics/AnalyticsEvents';
import { addSiteAlert, SiteAlertColors } from '../../slice/siteAlertsSlice';
import { CollapseButton, CollapseContent, CollapsePanel } from '../collapse-panel';
import LoadingPlaceholder from '../LoaderContainer/LoadingPlaceholder';
import { Comment } from '../utr-confirmation-modal/Comment';
import { QuestionStatus } from './QuestionStatus';
import './assurance-question-view.scss';
import '../utr-confirmation-modal/comments.scss';
import { DisputeButton } from './toolbar-buttons/DisputeButton';
import { VerifiedBadge } from './VerifiedBadge';
import { useQuestionIds } from '../../hooks/useQuestionIds';
import { ShowAs } from '../../reducers/universal-tracker-modal';
import { UniversalTrackerType } from '../../model/UniversalTracker';
import { QUESTION } from '../../constants/terminology';
import { RichTextEditorContainer } from '../rich-text-editor/RichTextEditor';

interface Props {
  assurancePortfolio: OrganizationAssurancePortfolio;
  surveyGroups: ScopeQuestionGroup[];
  questionId: string,
  index?: string;
  handleReload: () => void;
}

const defaultHistory: {
  stakeholderHistory?: { note?: string };
  verifierHistory?: { note?: string },
  loaded: boolean;
} = {
  stakeholderHistory: {},
  verifierHistory: {},
  loaded: false
};

export const AssuranceQuestionView = (props: Props) => {

  const { assurancePortfolio, surveyGroups, questionId, index, handleReload } = props;

  const [evidenceFiles, setEvidenceFiles] = useState<{ loaded: boolean, data?: ExistingEvidenceFile[] }>({ loaded: false });
  const [openProvenanceModal, setOpenProvenanceModal] = useState(false);
  const [openComments, setOpenComments] = useState(false);
  const [utrvHistory, setUtrvHistory] = useState(defaultHistory);
  const [commentId, setCommentId] = useState('');

  const dispatch = useAppDispatch();
  const history = useHistory();
  const location = useLocation();
  const assurancePortfolioId = assurancePortfolio._id;
  const surveyId = assurancePortfolio.surveyId;

  const assuranceUtrv = useMemo(
    () => assurancePortfolio.universalTrackerValueAssurances.find(a => a.utrvId === questionId),
    [assurancePortfolio, questionId]
  );

  useEffect(() => {
    if (location.search) {
      const searchParams = new URLSearchParams(location.search);
      const id = searchParams.get('commentId');
      if (id) {
        setCommentId(id);
        setOpenComments(!!id);
        searchParams.delete('commentId');
        history.replace({
          pathname: location.pathname,
          search: searchParams.toString(),
        });
      }
    }
  }, [history, location.pathname, location.search]);

  const { questionListIds, utr, utrv, altCode } = useQuestionIds(surveyGroups, index, questionId);

  React.useEffect(() => {
    loadUtrvHistoryAssurer(assurancePortfolioId, questionId)
      .then((respData: UtrvHistory) => {
        setEvidenceFiles({
          data: getExistingFiles(respData),
          loaded: true
        });

        const { stakeholderHistory, verifierHistory } = respData.latestHistory;
        setUtrvHistory({ stakeholderHistory, verifierHistory, loaded: true })
      })
      .catch(() => setEvidenceFiles({ loaded: true }));
  }, [assurancePortfolioId, questionId]);

  const goToQuestion = ({ id, index }: { id: any, index?: number }) => {
    const url = generateUrl(ROUTES.ASSURANCE_PORTFOLIO, {
      assurancePortfolioId,
      questionId: id,
      index: String(index ?? '')
    })
    history.push({ pathname: url, search: location.search });
  };

  const goNext = () => goToQuestion(getQuestionId(questionListIds, questionId, index ?? '', true));
  const goPrevious = () => goToQuestion(getQuestionId(questionListIds, questionId, index ?? '', false));

  const handleAssureQuestion = async () => {
    if (!assuranceUtrv) {
      return;
    }
    await G17Client.assureQuestions(assurancePortfolioId, { questions: [assuranceUtrv?._id] });
    handleReload();
  }

  const handleDownloadData = () => {
    if (!assuranceUtrv) {
      return;
    }
    downloadQuestionBundle(assurancePortfolioId, assuranceUtrv?._id)
      .then(async ([document]) => {
        if (!document || !document.url) {
          return;
        }
        window.open(document.url, '_blank', '');

        const analytics = getAnalytics();
        return analytics.track(AnalyticsEvents.SurveyDataDownloaded, {
          assurancePortfolioId,
          initiativeId: assurancePortfolio.initiativeId,
          questionId,
          source: 'survey_assurance'
        });
      })
      .catch((e: Error) => dispatch(addSiteAlert({
        content: `Unable to fetch requested data (${e.message})`,
        timeout: 3000,
        color: SiteAlertColors.Danger
      })))
  }

  const onBackClick = () => history.push(generateUrl(ROUTES.ASSURANCE_PORTFOLIO, { assurancePortfolioId }));
  const backSection = <div>
    <Dashboard>
      <div className='d-flex col' style={{ justifyContent: 'space-between' }}>
        <Button color='link' onClick={onBackClick}>
          <i className='fa fa-arrow-circle-left mr-2' />Back to {QUESTION.CAPITALIZED_SINGULAR} List
        </Button>
        <NavigationButtons goNext={goNext} goPrevious={goPrevious} isDisabled={questionListIds.length <= 1} />
      </div>
    </Dashboard>
  </div>;

  if (!utr || !utrv) {
    return (
      <div className='flex-column'>
        {backSection}
        <Dashboard className='assuranceContainer'>
          <DashboardSection padding={0}>
            <div className='p-3'>
              <h5>{QUESTION.CAPITALIZED_SINGULAR} not available</h5>
            </div>
          </DashboardSection>
        </Dashboard>
      </div>
    )
  }

  const standardName = standards[altCode]?.name || '';
  const standardCode = utr.getTypeCode(altCode);

  const naType = getNaType(utrv);

  const isAssured = assuranceUtrv?.status === AssuranceStatus.Completed;
  const isVerified = utrv.status === UtrvStatus.Verified;
  const canAssure = isVerified && !isAssured;
  const isCompletedAssurance = assurancePortfolio.status === AssurancePortfolioStatus.Completed;

  const instructionsText = `View ${standards[altCode]?.shortName || ''} Instructions`;
  const instructionsLink = utr.getInstructionsLink(altCode);

  const openUtrModal = () => {
    if (!utrv) {
      return;
    }
    return dispatch(
      loadUniversalTrackerModal(
        {
          universalTrackerId: utrv.universalTrackerId,
          universalTrackerType: UniversalTrackerType.Utr,
          actionValueId: utrv._id,
          params: { surveyId },
          openAction: { activeTabId: ShowAs.Table, hideProvenance: true }
        })
    );
  }
  const handleToggle = () => {
    setCommentId('');
    setOpenComments(!openComments);
  };
  const hasComments = [utrvHistory.stakeholderHistory, utrvHistory.verifierHistory].some(h => h?.note);
  return (
    <div className='flex-column'>
      {backSection}
      <Dashboard className='assurance-question-view'>
        <DashboardSection padding={0}>
          {utrv && openComments ? <div className='pt-1 position-relative'>
            <UtrvComments toggle={handleToggle} utrvId={utrv._id} commentId={commentId} />
          </div> : null}
          <div className='p-3'>

            <div className='mb-3 d-relative text-ThemeTextMedium'>
              <div className='d-flex'>
                <div>
                  {`${standardName} - ${standardCode}`}
                </div>
                <div className='d-flex align-items-center ml-auto text-right'>
                  <QuestionStatus assuranceUtrv={assuranceUtrv} />
                  <VerifiedBadge isVerified={isVerified} />
                  <NotApplicableBadge notReported={naType} showExplanation={false} />
                  <Button size='sm' outline className='ml-2' onClick={handleToggle}>
                    <i className='far fa-comment-alt' />
                  </Button>
                  {instructionsLink &&
                    <div>
                      <Button color='link' size='sm'
                        onClick={() => window.open(instructionsLink, '_blank')}>
                        <u>{instructionsText}</u>
                      </Button>
                    </div>
                  }
                </div>
              </div>
            </div>

            <div className='mt-3 pt-2 strong text-xl'>
              {utr.getValueLabel(altCode)}
            </div>

            <div className='mt-3'>
              {nl2br(utr.getInstructions(altCode))}
            </div>

            <div className='mt-4'>
              <QuestionInput utrv={utrv} utr={utr} isReadOnly={true} />
              <div className='text-ThemeAccentMedium text-right'>
                <Button color='link' disabled={!utrv} size='sm' onClick={() => openUtrModal()}>
                  <i className='fas fa-history text-ThemeAccentMedium mr-2' />data history
                </Button>
                <Button color='link' disabled={!utrv} size='sm' onClick={() => setOpenProvenanceModal(!openProvenanceModal)}>
                  <i className='fas fa-clipboard text-ThemeAccentMedium mr-2' />provenance
                </Button>
                {openProvenanceModal &&
                  // @ts-ignore
                  <CompositeValueHistory isOpen={openProvenanceModal}
                    ids={[utrv._id]}
                    toggle={() => setOpenProvenanceModal(!openProvenanceModal)} />}
              </div>
            </div>

            {(evidenceFiles.data ?? []).length > 0 ?
              <>
                <div className='mt-4 pt-2'>
                  <h5 className='d-flex align-items-center gap-3'>
                    Supporting Evidence
                  <SupportingEvidenceTooltip />
                  </h5>
                </div>
                <div>
                  <EvidenceContainer
                    files={evidenceFiles.data ?? []}
                    allowToggle={false}
                    isLoaded={true}
                    saving={false}
                  />
                </div>
              </>
              :
              null
            }
            {hasComments ?
              <CollapsePanel className='comment-group comments mt-3 py-3 border-top border-bottom' collapsed>
                <CollapseButton>
                  <h5 className='question-title-container mb-0'>
                    Further explanation / notes
                  </h5>
                </CollapseButton>
                <CollapseContent>
                  <RichTextEditorContainer>
                  <LoadingPlaceholder height={85} isLoading={!utr}>
                    {[utrvHistory.stakeholderHistory, utrvHistory.verifierHistory].map((history, index) => {
                      return history?.note ? (
                        <Comment
                          key={index ? 'verifier' : 'stakeholder'}
                          history={history as Pick<ValueHistory, 'action' | 'note'>}
                          handleComments={() => {}}
                          canEdit={false}
                          canUseRichTextEditor={false}
                        />
                      ) : null;
                    })}
                  </LoadingPlaceholder>
                  </RichTextEditorContainer>
                </CollapseContent>
              </CollapsePanel>
              : null}

            <div className='mt-4 d-flex justify-content-between'>
              <Button outline disabled={!isVerified} className='p-2 px-3 mr-2' onClick={() => handleDownloadData()}>
                <i className='fa-regular fa-file-zipper mr-2' />Download data and evidence
              </Button>
              <div>
                <DisputeButton
                  isDisabled={isCompletedAssurance}
                  utrvId={utrv._id}
                  assurancePortfolioId={assurancePortfolioId}
                  assuranceUtrv={assuranceUtrv}
                  handleReload={handleReload}
                  organization={assurancePortfolio.organization}
                />
                <Button disabled={!canAssure || isCompletedAssurance} className='p-2 px-3' onClick={() => handleAssureQuestion()}>
                  Assure {QUESTION.SINGULAR}
                </Button>
              </div>
            </div>

          </div>

        </DashboardSection>
      </Dashboard>
    </div>
  )
};
